import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatStore, ChatMessageType, ChatSessionState } from './types';

const PLAN_SEPARATOR = '----------------------------------------';
const STREAM_FLUSH_MS = 155;

const streamControllers = new Map<string, AbortController>();

const getDefaultSession = (): ChatSessionState => ({
  messages: [],
  isStreaming: false,
  botStatus: null,
  isChatActive: false,
});

const normalizeTopicFromQuestion = (question: string): string => {
  const cleaned = question.replace(/\s+/g, ' ').trim();

  const associatedMatch = cleaned.match(/which\s+(.+?)\s+ordered by/i) ?? cleaned.match(/which\s+(.+?)\?/i);
  if (associatedMatch?.[1]) {
    return associatedMatch[1].trim();
  }

  return cleaned.replace(/\?$/, '').trim();
};

const normalizeReasoningTextFromQuestion = (question: string): string => {
  const cleaned = question.replace(/\s+/g, ' ').replace(/\?$/, '').trim();

  return cleaned
    .replace(/^which\s+/i, '')
    .replace(/^what\s+are\s+/i, '')
    .replace(/^what\s+is\s+/i, '')
    .replace(/^show\s+me\s+/i, '')
    .replace(/^list\s+/i, '')
    .trim();
};

const toReasoningStep = (tool: string, question: string): string | null => {
  const normalizedTool = tool.trim().toLowerCase();

  if (normalizedTool === 'disgenetapi') {
    const topic = normalizeTopicFromQuestion(question);
    return `Searching DISGENET for ${topic}`;
  }

  if (normalizedTool === 'redactor') {
    const focus = normalizeReasoningTextFromQuestion(question);
    return `Identifying ${focus || 'key findings'}`;
  }

  return null;
};

const parseReasoningSteps = (planText: string): string[] => {
  const taskRegex =
    /\*\*\s*Task ID:\s*\d+\s*\|\s*[\s\S]*?Tool:\s*([^\n*]+)\*\*[\s\S]*?question:\s*([^\n]+(?:\n(?!\s*\*\*\s*Task ID:)[^\n]+)*)/gi;
  const steps: string[] = [];

  for (const match of planText.matchAll(taskRegex)) {
    const tool = match[1]?.trim();
    const question = match[2]?.replace(/\s+/g, ' ').trim();

    if (!tool || !question) {
      continue;
    }

    const step = toReasoningStep(tool, question);
    if (step) {
      steps.push(step);
    }
  }

  return steps;
};

const parsePlanAndAnswer = (
  rawText: string,
): { reasoning: string; reasoningSteps: string[]; answer: string } => {
  const planIndex = rawText.indexOf('Plan:');
  const separatorIndex = rawText.indexOf(PLAN_SEPARATOR);

  if (planIndex === -1 || separatorIndex === -1 || separatorIndex < planIndex) {
    return { reasoning: '', reasoningSteps: [], answer: rawText };
  }

  const reasoning = rawText.slice(planIndex + 'Plan:'.length, separatorIndex).trim();
  const reasoningSteps = parseReasoningSteps(reasoning);
  const answer = rawText.slice(separatorIndex + PLAN_SEPARATOR.length).trimStart();

  return { reasoning, reasoningSteps, answer };
};

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      chatHistory: [],
      chatSessions: {},

      setChatHistory: history => {
        set(state => ({
          chatHistory: typeof history === 'function' ? history(state.chatHistory) : history,
        }));
      },

      clearChatHistory: () => {
        set({ chatHistory: [] });
      },

      ensureSession: (chatId, initialBotMessage) => {
        set(state => {
          const existingSession = state.chatSessions[chatId];

          if (existingSession) {
            return state;
          }

          return {
            chatSessions: {
              ...state.chatSessions,
              [chatId]: {
                ...getDefaultSession(),
                messages: [initialBotMessage],
              },
            },
          };
        });
      },

      getSessionMessages: chatId => {
        return get().chatSessions[chatId]?.messages ?? [];
      },

      addUserMessage: (chatId, message) => {
        set(state => {
          const session = state.chatSessions[chatId] ?? getDefaultSession();
          const updatedMessages = [...session.messages, message];

          return {
            chatHistory: updatedMessages,
            chatSessions: {
              ...state.chatSessions,
              [chatId]: {
                ...session,
                messages: updatedMessages,
                isChatActive: true,
              },
            },
          };
        });
      },

      clearSession: (chatId, initialBotMessage) => {
        set(state => ({
          chatHistory: [initialBotMessage],
          chatSessions: {
            ...state.chatSessions,
            [chatId]: {
              ...getDefaultSession(),
              messages: [initialBotMessage],
            },
          },
        }));
      },

      streamBotResponse: async ({ chatId, userMessage, userApiKey }) => {
        const currentTime = new Date();
        const controller = new AbortController();

        const previousController = streamControllers.get(chatId);
        if (previousController) {
          previousController.abort();
        }
        streamControllers.set(chatId, controller);

        const botMessageId = `bot-${currentTime.getTime()}`;
        const headers = { Authorization: userApiKey };
        const url = `${process.env.REACT_APP_URL_CHAT_API}get?msg=${encodeURIComponent(userMessage)}`;

        let rawText = '';
        let flushTimeout: ReturnType<typeof setTimeout> | null = null;

        const flushMessage = () => {
          const { reasoning, reasoningSteps, answer } = parsePlanAndAnswer(rawText);

          set(state => {
            const session = state.chatSessions[chatId] ?? getDefaultSession();
            const updatedMessages = session.messages.map(msg =>
              msg.id === botMessageId ? { ...msg, message: answer, reasoning, reasoningSteps } : msg,
            );

            return {
              chatHistory: updatedMessages,
              chatSessions: {
                ...state.chatSessions,
                [chatId]: {
                  ...session,
                  messages: updatedMessages,
                },
              },
            };
          });
        };

        const scheduleFlush = () => {
          if (flushTimeout) {
            return;
          }

          flushTimeout = setTimeout(() => {
            flushMessage();
            flushTimeout = null;
          }, STREAM_FLUSH_MS);
        };

        set(state => {
          const session = state.chatSessions[chatId] ?? getDefaultSession();

          return {
            chatSessions: {
              ...state.chatSessions,
              [chatId]: {
                ...session,
                messages: [
                  ...session.messages,
                  { id: botMessageId, sender: 'bot', message: '', timestamp: new Date() },
                ],
                isStreaming: true,
                botStatus: 'thinking',
                isChatActive: true,
              },
            },
          };
        });

        try {
          const response = await fetch(url, {
            method: 'GET',
            headers,
            credentials: 'include',
            signal: controller.signal,
            keepalive: true,
          });

          if (!response.ok) {
            if (response.status === 504) {
              throw new Error(
                'The request is taking longer than expected. Please try a simpler question or try again later.',
              );
            }
            throw new Error('Network response was not ok');
          }

          if (!response.body) {
            throw new Error('No response body received');
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let done = false;

          while (!done) {
            if (controller.signal.aborted) {
              break;
            }

            const { value, done: chunkDone } = await reader.read();
            done = chunkDone;

            if (value) {
              rawText += decoder.decode(value, { stream: true });
              scheduleFlush();
            }
          }

          if (flushTimeout) {
            clearTimeout(flushTimeout);
            flushTimeout = null;
          }
          flushMessage();
        } catch (error: unknown) {
          if (error instanceof Error && error.name === 'AbortError') {
            // Request intentionally stopped by user.
          } else {
            const errorMessage =
              error instanceof Error && error.message.includes('taking longer than expected')
                ? error.message
                : 'Sorry, there was an error processing your request. Please try again.';

            const errorMsg: ChatMessageType = {
              id: `bot-error-${currentTime.getTime()}`,
              sender: 'bot',
              message: errorMessage,
              timestamp: new Date(),
            };

            set(state => {
              const session = state.chatSessions[chatId] ?? getDefaultSession();
              const updatedMessages = [...session.messages, errorMsg];

              return {
                chatHistory: updatedMessages,
                chatSessions: {
                  ...state.chatSessions,
                  [chatId]: {
                    ...session,
                    messages: updatedMessages,
                  },
                },
              };
            });
          }
        } finally {
          if (flushTimeout) {
            clearTimeout(flushTimeout);
          }

          if (streamControllers.get(chatId) === controller) {
            streamControllers.delete(chatId);
          }

          set(state => {
            const session = state.chatSessions[chatId] ?? getDefaultSession();
            return {
              chatSessions: {
                ...state.chatSessions,
                [chatId]: {
                  ...session,
                  isStreaming: false,
                  botStatus: null,
                },
              },
            };
          });
        }
      },

      stopStreaming: chatId => {
        const controller = streamControllers.get(chatId);
        if (controller) {
          controller.abort();
          streamControllers.delete(chatId);
        }

        set(state => {
          const session = state.chatSessions[chatId];
          if (!session) {
            return state;
          }

          return {
            chatSessions: {
              ...state.chatSessions,
              [chatId]: {
                ...session,
                isStreaming: false,
                botStatus: null,
              },
            },
          };
        });
      },
    }),
    {
      name: 'chat-storage', // localStorage key
      partialize: state => ({ chatHistory: state.chatHistory, chatSessions: state.chatSessions }),
    },
  ),
);
